import { useState, useEffect, useCallback } from 'react';
import {producaov2 } from '../../../services/apis';
import swal from 'sweetalert';

export const LOGS_FILTERS = {
  SEARCH: 'search',
  ID_INCOMPANY: 'id_incompany',
  DATE_START: 'date_start',
  DATE_END: 'date_end',
};

const buildResumosUrl = ({ baseUrl, page, rowsPerPage, filters }) => {
  const query = {
    page: page + 1, 
    limit: rowsPerPage,
    ...(filters?.[LOGS_FILTERS.SEARCH] && { search: filters[LOGS_FILTERS.SEARCH] }),
    ...(filters?.[LOGS_FILTERS.ID_INCOMPANY] && { id_incompany: filters[LOGS_FILTERS.ID_INCOMPANY] }),
    ...(filters?.[LOGS_FILTERS.DATE_START] && { dateStart: filters[LOGS_FILTERS.DATE_START] }),
    ...(filters?.[LOGS_FILTERS.DATE_END] && { dateEnd: filters[LOGS_FILTERS.DATE_END] }),
  };

  const queryParams = new URLSearchParams(query);
  return `${baseUrl}?${queryParams.toString()}`;
};

const formatDateToLocalInput = (date) => {
  const pad = (num) => String(num).padStart(2, '0');
  
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Mês é 0-indexado
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const endDate = new Date();
endDate.setHours(23, 59, 59, 999);
const startDate = new Date();
startDate.setHours(0, 0, 0, 0);
startDate.setDate(endDate.getDate() - 7);

const initialFilters = {
  [LOGS_FILTERS.DATE_START]: formatDateToLocalInput(startDate),
  [LOGS_FILTERS.DATE_END]: formatDateToLocalInput(endDate),
  [LOGS_FILTERS.ID_INCOMPANY]: 'Todas as Empresas',
};

const useSystemLogs = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0); 
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentFilters, setCurrentFilters] = useState(initialFilters);
  const [incompanys, setIncompanys] = useState([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const url = buildResumosUrl({
      baseUrl: '/admin/log/list',
      page: currentPage,
      rowsPerPage,
      filters: currentFilters
    });
    try {
      const response = await producaov2.get(url);
      setData(response.data || []);
      const uniqueCompaniesMap = new Map();
      (response.data || [])
        .filter(item => item && item.company)
        .forEach(item => {
          if (!uniqueCompaniesMap.has(item.company.id)) {
            uniqueCompaniesMap.set(item.company.id, {
              id: item.company.id,
              name: item.company.name
            });
          }
        });
      
      setIncompanys(Array.from(uniqueCompaniesMap.values()));
      const total = response.headers?.['x-total-count'] || response.headers?.['X-Total-Count'];
      setTotalRecords(Number(total) || 0);
    } catch (error) {
      console.error("Erro ao buscar logs do sistema:", error);
      setData([]);
      setIncompanys([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, rowsPerPage, currentFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPageValue) => {
    setRowsPerPage(parseInt(newRowsPerPageValue, 10));
    setCurrentPage(0);
  };

  const handleSubmitFilters = (newFilters) => {
    const startDate = newFilters[LOGS_FILTERS.DATE_START];
    const endDate = newFilters[LOGS_FILTERS.DATE_END];
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      swal({
        title: "Data Inválida!",
        text: "A data de início não pode ser maior que a data de fim.",
        icon: "error",
        button: "Ok",
      });
      return;
    }
    setCurrentFilters(newFilters);
    setCurrentPage(0);
  };

  const handleResetFilters = () => {
    setCurrentFilters(initialFilters);
    setCurrentPage(0);
  };

  const refreshData = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    incompanys,
    totalRecords,
    isLoading,
    currentPage,
    rowsPerPage,
    currentFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleSubmitFilters,
    handleResetFilters,
    refreshData,
  };
};

export default useSystemLogs;